import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useDeleteConfirmDialog } from "@base/model/hooks";
import useIntersectionObserver from "@base/model/hooks/useIntersectionObserver";
import { ResponsiveSheet } from "@base/ui/ResponsiveOverlay";
import { useSafeAreaInset } from "@base/ui/SafeArea/useSafeAreaInset";
import { Button, Dropdown, TextField } from "@buildblockinc/buildblock-design-system";
import { ErrorBoundary } from "@sentry/react";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { EllipsisVertical, MessageSquareOff, SendHorizontal, X } from "lucide-react";
import { Suspense, memo, useCallback, useEffect } from "react";
import type { Comment } from "../model/schema";
import { useInfiniteCommentsQuery } from "../model/useCommentsQuery";
import { useCreateCommentMutation } from "../model/useCreateCommentMutation";
import { useDeleteCommentMutation } from "../model/useDeleteCommentMutation";

dayjs.extend(relativeTime);

interface CommentsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  logId: string;
}

export function CommentsBottomSheet({ isOpen, onClose, projectId, logId }: CommentsBottomSheetProps) {
  return (
    <ResponsiveSheet isOpen={isOpen} onClose={onClose} maxHeight={100} ariaLabel="Comments">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center px-4 pb-4 bg-white shrink-0 md:py-4">
          <h2 className="text-title6 text-gray-1000">Comments</h2>
          <button type="button" onClick={onClose} aria-label="Close comments">
            <X width={24} height={24} className="text-gray-1000" />
          </button>
        </div>

        {isOpen ? (
          <ErrorBoundary
            fallback={({ resetError }) => <CommentListError projectId={projectId} logId={logId} onRetry={resetError} />}
          >
            <Suspense fallback={<CommentListSkeleton />}>
              <CommentList projectId={projectId} logId={logId} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <div className="flex-1" />
        )}

        <CommentInput projectId={projectId} logId={logId} />
      </div>
    </ResponsiveSheet>
  );
}

interface CommentListProps {
  projectId: string;
  logId: string;
}

function CommentList({ projectId, logId }: CommentListProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteCommentsQuery(projectId, logId);

  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver();

  const { openDeleteDialog, close } = useDeleteConfirmDialog();
  const { mutateAsync: deleteComment } = useDeleteCommentMutation(projectId, logId, {
    onSettled: () => {
      close();
    },
  });

  const handleDelete = useCallback(
    (commentId: number) => {
      openDeleteDialog({
        description: "You will be unable to recover this comment.",
        onDelete: async () => {
          await deleteComment(commentId);
          EventLogger.result(CLIENT_EVENT.COMMENT_DELETE, { project_id: projectId, log_id: logId });
        },
      });
    },
    [openDeleteDialog, deleteComment, projectId, logId],
  );

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const comments = data.pages.flatMap((page) => page.results);

  if (comments.length === 0) {
    return (
      <div className="flex flex-col flex-1 gap-2 justify-center items-center pb-4 text-center">
        <MessageSquareOff width={30} height={30} className="text-gray-300" strokeWidth={1.5} />
        <p className="text-gray-500 text-body3">No comments yet.</p>
      </div>
    );
  }

  return (
    <div className="flex overflow-y-auto flex-col flex-1 scrollbar-hide">
      <ul className="flex flex-col gap-y-3 px-4 pb-4">
        {comments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} onDelete={handleDelete} />
        ))}
      </ul>

      <div ref={sentinelRef as (instance: HTMLDivElement | null) => void} className="h-1 shrink-0" />

      {isFetchingNextPage && (
        <div className="flex justify-center py-3 shrink-0">
          <div className="w-5 h-5 rounded-full border-2 border-gray-300 animate-spin border-t-gray-600" />
        </div>
      )}
    </div>
  );
}

function CommentListSkeleton() {
  return (
    <ul className="flex overflow-y-auto flex-col flex-1 gap-y-3 px-4 pb-4 animate-pulse">
      {Array.from({ length: 4 }, (_, i) => `comment-skeleton-${i}`).map((key) => (
        <li key={key} className="p-4 bg-gray-10 rounded-8">
          <div className="flex gap-x-2 items-center mb-4">
            <div className="w-6 h-6 rounded-full skeleton-shimmer shrink-0" />
            <div className="flex-1 h-4 rounded-4 skeleton-shimmer" />
            <div className="w-14 h-3 rounded-4 skeleton-shimmer" />
          </div>
          <div className="flex flex-col gap-y-2">
            <div className="h-3 rounded-4 skeleton-shimmer" />
            <div className="w-3/4 h-3 rounded-4 skeleton-shimmer" />
          </div>
        </li>
      ))}
    </ul>
  );
}

interface CommentListErrorProps {
  projectId: string;
  logId: string;
  onRetry: () => void;
}

function CommentListError({ projectId, logId, onRetry }: CommentListErrorProps) {
  const queryClient = useQueryClient();

  const handleRetry = () => {
    queryClient.resetQueries({ queryKey: ["comments", projectId, logId] });
    onRetry();
  };

  return (
    <div className="flex flex-col flex-1 gap-3 justify-center items-center px-4 pb-4 text-center">
      <p className="text-gray-700 text-body2">Failed to load comments.</p>
      <Button onClick={handleRetry} variant="outline" color="info">
        Try Again
      </Button>
    </div>
  );
}

const CommentItem = memo(function CommentItem({
  comment,
  onDelete,
}: { comment: Comment; onDelete: (id: number) => void }) {
  const initial = comment.user.name[0]?.toUpperCase() ?? "?";
  const isOwner = comment.is_owner;

  return (
    <li className="p-4 bg-gray-10 rounded-8">
      <div className="flex gap-x-2 items-center mb-4">
        {comment.user.avatar_url ? (
          <img
            src={comment.user.avatar_url}
            alt={comment.user.name}
            className="object-cover object-top w-6 h-6 rounded-full shrink-0"
          />
        ) : (
          <div className="flex justify-center items-center w-6 h-6 bg-gray-200 rounded-full shrink-0">
            <span className="text-[10px] text-gray-600 font-medium">{initial}</span>
          </div>
        )}
        <span className="flex-1 text-subtitle6 text-gray-1000">{comment.user.name}</span>
        <div className="flex gap-x-1 items-center">
          <span className="text-gray-500 text-caption1">{dayjs(comment.write_time).fromNow()}</span>
          {isOwner && (
            <Dropdown>
              <Dropdown.Trigger className="p-1 shrink-0">
                <EllipsisVertical width={20} height={20} className="text-gray-1000" />
              </Dropdown.Trigger>
              <Dropdown.Content className="p-2 z-[200]">
                <Dropdown.Item value="delete">
                  <Button
                    type="button"
                    className="px-2 py-3 text-red-500"
                    variant="ghost"
                    color="secondary"
                    size="large"
                    onClick={() => onDelete(comment.id)}
                  >
                    <span className="leading-none">Delete Comment</span>
                  </Button>
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown>
          )}
        </div>
      </div>
      <p className="whitespace-pre-wrap text-gray-1000 text-body3 line-clamp-3">{comment.content}</p>
    </li>
  );
});

interface CommentInputProps {
  projectId: string;
  logId: string;
}

const CommentInput = ({ projectId, logId }: CommentInputProps) => {
  const { mutate: createComment, isPending, isError } = useCreateCommentMutation(projectId, logId);

  const form = useForm({
    defaultValues: { content: "" },
    onSubmit: ({ value }) => {
      const trimmedContent = value.content.trim();
      if (!trimmedContent) return;

      createComment(trimmedContent, {
        onSuccess: () => {
          EventLogger.result(CLIENT_EVENT.COMMENT_SUBMIT_SUCCESS, {
            project_id: projectId,
            log_id: logId,
            content_length: trimmedContent.length,
          });
          form.reset();
        },
        onError: () => {
          EventLogger.result(CLIENT_EVENT.COMMENT_SUBMIT_FAIL, {
            project_id: projectId,
            log_id: logId,
            content_length: trimmedContent.length,
          });
        },
      });
    },
  });

  const safeArea = useSafeAreaInset();

  return (
    <div style={{ paddingBottom: `calc(1rem + ${safeArea.bottom}px)` }} className="p-4 bg-white shrink-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="content">
          {(field) => (
            <TextField>
              <TextField.Input
                placeholder="Leave a comment"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                disabled={isPending}
                suffix={
                  isPending ? (
                    <div className="w-4 h-4 rounded-full border-2 border-blue-300 animate-spin border-t-blue-600 shrink-0" />
                  ) : (
                    <button
                      type="submit"
                      aria-label="Send comment"
                      className="shrink-0"
                      disabled={!field.state.value.trim()}
                    >
                      <SendHorizontal
                        className={field.state.value.trim() ? "text-blue-500" : "text-gray-300"}
                        width={16}
                        height={16}
                      />
                    </button>
                  )
                }
              />
            </TextField>
          )}
        </form.Field>
      </form>
      {isError && <p className="mt-2 text-red-500 text-caption1">Failed to send comment. Please try again.</p>}
    </div>
  );
};
